import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Loader2, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Partner {
  id: string;
  upfrontCommissionRate: string | null;
  trailCommissionRate: string | null;
}

interface SplitRate {
  id: string;
  partnerId: string;
  lenderId: string | null;
  productType: string | null;
  upfrontSplitPercent: string;
  trailSplitPercent: string;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  isDefault: boolean;
  notes: string | null;
}

interface SplitRateFormData {
  lenderId: string;
  productType: string;
  upfrontSplitPercent: string;
  trailSplitPercent: string;
  effectiveFrom: string;
  effectiveTo: string;
  notes: string;
}

interface PartnerSplitConfigPanelProps {
  partnerId: string;
  partner: Partner | null;
}

// ---------------------------------------------------------------------------
// Default form state
// ---------------------------------------------------------------------------

const DEFAULT_FORM: SplitRateFormData = {
  lenderId: "",
  productType: "",
  upfrontSplitPercent: "",
  trailSplitPercent: "",
  effectiveFrom: "",
  effectiveTo: "",
  notes: "",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PartnerSplitConfigPanel({ partnerId, partner }: PartnerSplitConfigPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<SplitRate | null>(null);
  const [formData, setFormData] = useState<SplitRateFormData>(DEFAULT_FORM);
  const [deleteRate, setDeleteRate] = useState<SplitRate | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: splitRates = [], isLoading } = useQuery<SplitRate[]>({
    queryKey: ["/api/commissions/partners", partnerId, "split-rates"],
    enabled: !!partnerId,
  });

  // Non-default override rows
  const overrideRates = splitRates.filter((r) => !r.isDefault);
  const defaultRate = splitRates.find((r) => r.isDefault);

  const handleOpenAdd = () => {
    setEditingRate(null);
    setFormData(DEFAULT_FORM);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (rate: SplitRate) => {
    setEditingRate(rate);
    setFormData({
      lenderId: rate.lenderId ?? "",
      productType: rate.productType ?? "",
      upfrontSplitPercent: rate.upfrontSplitPercent,
      trailSplitPercent: rate.trailSplitPercent,
      effectiveFrom: rate.effectiveFrom ?? "",
      effectiveTo: rate.effectiveTo ?? "",
      notes: rate.notes ?? "",
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRate(null);
    setFormData(DEFAULT_FORM);
  };

  const createMutation = useMutation({
    mutationFn: async (data: SplitRateFormData) => {
      const payload: Record<string, unknown> = {
        upfrontSplitPercent: data.upfrontSplitPercent,
        trailSplitPercent: data.trailSplitPercent,
        isDefault: false,
      };
      if (data.lenderId) payload.lenderId = data.lenderId;
      if (data.productType) payload.productType = data.productType;
      if (data.effectiveFrom) payload.effectiveFrom = data.effectiveFrom;
      if (data.effectiveTo) payload.effectiveTo = data.effectiveTo;
      if (data.notes) payload.notes = data.notes;

      await apiRequest("POST", `/api/commissions/partners/${partnerId}/split-rates`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/commissions"] });
      toast({ title: "Split rate created" });
      handleCloseModal();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create split rate",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SplitRateFormData }) => {
      const payload: Record<string, unknown> = {
        upfrontSplitPercent: data.upfrontSplitPercent,
        trailSplitPercent: data.trailSplitPercent,
      };
      if (data.lenderId !== undefined) payload.lenderId = data.lenderId || null;
      if (data.productType !== undefined) payload.productType = data.productType || null;
      if (data.effectiveFrom !== undefined) payload.effectiveFrom = data.effectiveFrom || null;
      if (data.effectiveTo !== undefined) payload.effectiveTo = data.effectiveTo || null;
      if (data.notes !== undefined) payload.notes = data.notes || null;

      await apiRequest("PATCH", `/api/commissions/split-rates/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/commissions"] });
      toast({ title: "Split rate updated" });
      handleCloseModal();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update split rate",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/commissions/split-rates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/commissions"] });
      toast({ title: "Split rate deleted" });
      setDeleteRate(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete split rate",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    if (!formData.upfrontSplitPercent || !formData.trailSplitPercent) {
      toast({
        title: "Validation error",
        description: "Upfront and trail split % are required",
        variant: "destructive",
      });
      return;
    }

    if (editingRate) {
      updateMutation.mutate({ id: editingRate.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="panel-partner-split-config">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">
          COMMISSION SPLIT CONFIGURATION
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={handleOpenAdd}
          data-testid="btn-add-split-rate"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Override
        </Button>
      </div>

      {/* Cascade explanation */}
      <div className="flex items-start gap-2 p-3 mb-3 bg-muted/50 rounded-md text-xs text-muted-foreground">
        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
        <span>
          Split resolution priority: <strong>Product + Lender</strong> &gt;{" "}
          <strong>Lender only</strong> &gt; <strong>Partner default</strong>.
          The most specific matching override is applied.
        </span>
      </div>

      {/* Default row from partner record */}
      <div className="border rounded-md mb-3">
        <Table>
          <TableHeader>
            <TableRow className="h-10 bg-muted/50">
              <TableHead className="text-xs font-medium px-2 py-1">Lender</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1">Product Type</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1 text-right">Upfront %</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1 text-right">Trail %</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1">From</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1">To</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1">Type</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1 w-[90px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Default row: either from explicit isDefault rate or from partner record */}
            {defaultRate ? (
              <TableRow className="h-10 bg-blue-50/50 dark:bg-blue-950/20" data-testid={`row-split-rate-${defaultRate.id}`}>
                <TableCell className="px-2 py-1 text-sm text-muted-foreground">All Lenders</TableCell>
                <TableCell className="px-2 py-1 text-sm text-muted-foreground">All Products</TableCell>
                <TableCell className="px-2 py-1 text-sm text-right">{defaultRate.upfrontSplitPercent}%</TableCell>
                <TableCell className="px-2 py-1 text-sm text-right">{defaultRate.trailSplitPercent}%</TableCell>
                <TableCell className="px-2 py-1 text-sm">—</TableCell>
                <TableCell className="px-2 py-1 text-sm">—</TableCell>
                <TableCell className="px-2 py-1">
                  <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-700">Default</Badge>
                </TableCell>
                <TableCell className="px-2 py-1 text-muted-foreground text-xs">Cannot delete</TableCell>
              </TableRow>
            ) : (
              <TableRow className="h-10 bg-blue-50/50 dark:bg-blue-950/20">
                <TableCell className="px-2 py-1 text-sm text-muted-foreground">All Lenders</TableCell>
                <TableCell className="px-2 py-1 text-sm text-muted-foreground">All Products</TableCell>
                <TableCell className="px-2 py-1 text-sm text-right">
                  {partner?.upfrontCommissionRate ? `${partner.upfrontCommissionRate}%` : "—"}
                </TableCell>
                <TableCell className="px-2 py-1 text-sm text-right">
                  {partner?.trailCommissionRate ? `${partner.trailCommissionRate}%` : "—"}
                </TableCell>
                <TableCell className="px-2 py-1 text-sm">—</TableCell>
                <TableCell className="px-2 py-1 text-sm">—</TableCell>
                <TableCell className="px-2 py-1">
                  <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-700">
                    Using default rates
                  </Badge>
                </TableCell>
                <TableCell className="px-2 py-1 text-muted-foreground text-xs">Cannot delete</TableCell>
              </TableRow>
            )}

            {/* Override rows */}
            {overrideRates.map((rate) => (
              <TableRow key={rate.id} className="h-10" data-testid={`row-split-rate-${rate.id}`}>
                <TableCell className="px-2 py-1 text-sm">
                  {rate.lenderId ? <span className="font-mono text-xs">{rate.lenderId.slice(0, 8)}…</span> : "All Lenders"}
                </TableCell>
                <TableCell className="px-2 py-1 text-sm capitalize">{rate.productType ?? "All Products"}</TableCell>
                <TableCell className="px-2 py-1 text-sm text-right">{rate.upfrontSplitPercent}%</TableCell>
                <TableCell className="px-2 py-1 text-sm text-right">{rate.trailSplitPercent}%</TableCell>
                <TableCell className="px-2 py-1 text-sm">{rate.effectiveFrom ?? "—"}</TableCell>
                <TableCell className="px-2 py-1 text-sm">{rate.effectiveTo ?? "—"}</TableCell>
                <TableCell className="px-2 py-1">
                  <Badge variant="secondary" className="text-xs">Override</Badge>
                </TableCell>
                <TableCell className="px-2 py-1">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleOpenEdit(rate)}
                      data-testid={`btn-edit-split-rate-${rate.id}`}
                      aria-label="Edit"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setDeleteRate(rate)}
                      data-testid={`btn-delete-split-rate-${rate.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent data-testid="dialog-split-rate">
          <DialogHeader>
            <DialogTitle>
              {editingRate ? "Edit Split Rate Override" : "Add Split Rate Override"}
            </DialogTitle>
            <DialogDescription>
              {editingRate
                ? "Update the commission split rate override."
                : "Create a commission split rate override for specific lender or product combinations."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Lender ID (optional) */}
            <div className="space-y-1">
              <Label htmlFor="lenderId">Lender ID (optional)</Label>
              <Input
                id="lenderId"
                value={formData.lenderId}
                onChange={(e) => setFormData({ ...formData, lenderId: e.target.value })}
                placeholder="Leave blank for all lenders"
              />
            </div>

            {/* Product Type (optional) */}
            <div className="space-y-1">
              <Label htmlFor="productType">Product Type (optional)</Label>
              <Input
                id="productType"
                value={formData.productType}
                onChange={(e) => setFormData({ ...formData, productType: e.target.value })}
                placeholder="e.g. commercial, residential"
              />
            </div>

            {/* Upfront Split % */}
            <div className="space-y-1">
              <Label htmlFor="upfrontSplitPercent">Upfront Split % *</Label>
              <Input
                id="upfrontSplitPercent"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.upfrontSplitPercent}
                onChange={(e) => setFormData({ ...formData, upfrontSplitPercent: e.target.value })}
                placeholder="20.00"
                data-testid="input-upfront-split-percent"
              />
            </div>

            {/* Trail Split % */}
            <div className="space-y-1">
              <Label htmlFor="trailSplitPercent">Trail Split % *</Label>
              <Input
                id="trailSplitPercent"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.trailSplitPercent}
                onChange={(e) => setFormData({ ...formData, trailSplitPercent: e.target.value })}
                placeholder="10.00"
                data-testid="input-trail-split-percent"
              />
            </div>

            {/* Effective From */}
            <div className="space-y-1">
              <Label htmlFor="effectiveFrom">Effective From (optional)</Label>
              <Input
                id="effectiveFrom"
                type="date"
                value={formData.effectiveFrom}
                onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
              />
            </div>

            {/* Effective To */}
            <div className="space-y-1">
              <Label htmlFor="effectiveTo">Effective To (optional)</Label>
              <Input
                id="effectiveTo"
                type="date"
                value={formData.effectiveTo}
                onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
              />
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this override..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting || !formData.upfrontSplitPercent || !formData.trailSplitPercent}
              data-testid="btn-save-split-rate"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteRate} onOpenChange={(open) => !open && setDeleteRate(null)}>
        <AlertDialogContent data-testid="dialog-delete-split-rate">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Split Rate Override</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this split rate override? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteRate && deleteMutation.mutate(deleteRate.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirm-delete-split-rate"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
